import { Fragment, type ReactNode } from 'react';

/**
 * THE TASK EDITOR'S PANEL SWITCHES, AS ONE CONTROL.
 *
 * Variables, Flow and References open panels into the same slot, so at most one is ever on. Drawn
 * as loose buttons they were four unrelated things in a row; drawn as one segmented block they
 * read as what they are — the views of this task, with Preview beside them as the action.
 *
 * ## Why this is not `SegmentedControl`
 *
 * That one is a `radiogroup`: pick exactly one of degrees/radians, and something is always
 * chosen. This is a set of TOGGLES — each can be off, all can be off, and a screen reader should
 * hear "Flow, toggle button, not pressed", not "Flow, radio button, not checked". ARIA draws the
 * same line: `radiogroup` chooses a value, a `group` of `aria-pressed` buttons flips switches.
 * Bending one into the other would mean a mode flag that swaps the role, the state attribute and
 * whether the value may be null — two controls sharing a shell, which is how the hover grammar
 * drifted the last time. They are deliberately siblings, and share the container styling so they
 * still read as one family.
 *
 * ## Why it exists at all: the toolbar twins
 *
 * The rich-text toolbar sits about fifty pixels below this row, and it has a `{}` button for
 * inserting a variable while this row has a `{}` button for opening the Variables panel. Same
 * glyph, same size, same rounded square, entirely different jobs — and References adds a second
 * pair (`[[]]` to insert, a link to manage). A toolbar is a row of separate squares; grouping
 * these inside one container is the cue that says this row is not that one.
 */
export interface PanelToggleItem {
  id: string;
  icon: ReactNode;
  /**
   * The ACCESSIBLE name, always. It carries the state a badge can only show — "Flow needs
   * attention" rather than "Flow" — which is why it is not the visible text.
   */
  label: string;
  /**
   * The visible word, where there is room for one. Separate from `label` precisely because
   * `label` changes with state: rendering it would make the row's width jump the moment a rule
   * paused or a reference broke, which is a layout shift caused by a thing happening elsewhere.
   */
  text?: string;
  active: boolean;
  onToggle: () => void;
  disabled?: boolean;
  /**
   * A dot BESIDE THE GLYPH, for a panel holding something the teacher has not seen.
   *
   * `warning` is Flow's paused rule; `error` is a broken reference. The announcement is the
   * caller's `label`, which should already say so — a dot nobody can hear is half a signal.
   *
   * Inline rather than pinned to the segment's corner. The segments stretch, so a corner dot
   * ended up adrift in the middle of the row, nearer the NEXT switch's glyph than its own, and
   * a badge whose owner is ambiguous is worse than no badge.
   */
  badge?: 'warning' | 'error';
  /** Show `text` beside the icon. Off on phones, where the row has no width for words. */
  showLabel?: boolean;
}

const BADGE_TONE = {
  warning: 'bg-ws-warning-fg',
  error: 'bg-ws-error-fg',
} as const;

export function PanelToggleGroup({ items, ariaLabel, stretch = false, className = '' }: {
  items: PanelToggleItem[];
  ariaLabel: string;
  /**
   * Fill the row rather than shrinking to the glyphs.
   *
   * On by default nowhere, because the phone header slot shares its row with the title and the
   * save state. On the two rows that are the group's own — the desktop panel column and the
   * portrait sub-bar — a group sized to three 16px icons left most of the row to Preview and
   * made the switches look incidental beside it. Stretched, the group takes the larger share
   * and each switch gets a real target.
   */
  stretch?: boolean;
  className?: string;
}) {
  return (
    <div
      role='group'
      aria-label={ariaLabel}
      className={`flex gap-0.5 rounded-lg border border-ws-border bg-ws-btn p-0.5 ${stretch ? 'flex-1' : 'shrink-0'} ${className}`}
    >
      {items.map((item, i) => (
        <Fragment key={item.id}>
          {/* A hairline in the gap, suppressed either side of the filled segment so the active
              chip is never bisected. Always rendered, so the row's width does not twitch as
              panels open and close. */}
          {i > 0 && (
            <span
              aria-hidden='true'
              className={`my-1.5 w-px shrink-0 ${item.active || items[i - 1]?.active ? 'bg-transparent' : 'bg-ws-border'}`}
            />
          )}
        <button
          type='button'
          aria-pressed={item.active}
          aria-label={item.label}
          disabled={item.disabled}
          onClick={item.onToggle}
          className={`relative flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ws-primary disabled:pointer-events-none disabled:opacity-40 ${stretch ? 'flex-1' : ''} ${
            item.active
              ? 'bg-ws-btn-active text-ws-btn-active-fg'
              : 'text-ws-muted hover:bg-ws-hover hover:text-ws-text'
          }`}
        >
          {item.icon}
          {item.showLabel && item.text}
          {item.badge && (
            <span aria-hidden='true' className={`h-1.5 w-1.5 shrink-0 rounded-full ${BADGE_TONE[item.badge]}`} />
          )}
        </button>
        </Fragment>
      ))}
    </div>
  );
}
